/**
 * The chiller trip: the fault two rooms from the symptom, as a schedule.
 *
 * At two in the morning a chiller in the sub-fab trips. The process cooling
 * water runs warm - the house fault the building module already has - and
 * every chamber that rejects heat into that loop loses its setpoint and goes
 * down. On every etcher in the building that is two chambers of three. The
 * third, on its own chiller, keeps running. Lots keep arriving for etch at
 * the rate the whole line is set to, and the one chamber left cannot take
 * them.
 *
 * THE DECISION. The student is on shift and has three moves, and they are
 * the three a supervisor actually has:
 *
 *   reroute    let everything queue for the surviving chamber, in the order
 *              it arrives. The default - what happens if nobody decides.
 *   hold       stop starting lots until the chambers are back. Work the
 *              building does not have to carry, and output it will not get
 *              back. (Holding lots at the step BEFORE etch is the same queue
 *              under another name, and the model treats it that way - see
 *              Line.setHoldStarts.)
 *   by-date    run the bottleneck strictly by due date: whoever is nearest
 *              their date goes next, hot or not. (Hot lots already jump
 *              every queue under the other two - that is what hot means -
 *              so this is the one rule that can put a hot lot BEHIND an
 *              older lot with less slack.)
 *
 * WHAT THE MODEL FOUND, which is not what the story expected. Dispatching
 * by date loses fewer dates and delays fewer lots than first-come-first-
 * served at both loads, and drains etch faster, because nearest-date is
 * close to oldest-first and oldest-first pushes lots OUT of a re-entrant
 * line instead of round it again. At 85% it costs the hot lots nothing. At
 * 95% it costs them everything: the queue is long enough that a hot lot
 * that no longer jumps it misses its date, and most of them do. The rule
 * that is right at 85% is the rule that kills the hot lot at 95% - which
 * is the operating curve, seen as a decision. Holding starts does nothing
 * for the etch queue (the lots that will pile up are already in the
 * building) and costs the held lots six hours of slack each.
 *
 * Every policy runs on the same seed, so the arrivals, the service draws and
 * the failures are identical and the only difference between two results
 * is the decision. And every policy is laid over a CONTROL - the same seed
 * with no trip at all - lot by lot, which is what makes "lots delayed by
 * the fault" a number rather than a guess: a lot is delayed if it finished
 * later than it did in the control, and a date is missed because of the
 * fault if it was met in the control.
 *
 * THREE MORE SCENARIOS share the line and the control-run arithmetic:
 *
 *   runQtime      the same trip on a route where the resist has a shelf
 *                 life - lots not etched within the window go back to the
 *                 scanner. The one case where holding is right, if it is
 *                 done BEFORE the clock starts (the gate).
 *   runExcursion  no trip: one deposition tool goes silently wrong, and only
 *                 a sampled inspection can catch it. Lots exposed equals
 *                 throughput through that tool times the delay to detection -
 *                 Little's law again, from the yield side - and sampling
 *                 harder costs cycle time at the inspection tool.
 *   runPm         a chamber of the bottleneck is due maintenance. Do it now,
 *                 planned, or run on with a worn chamber that fails sooner
 *                 and for longer. Averaged over seeds, because a deferral
 *                 is a bet.
 *
 * WHAT THE RESULT SAYS. How high WIP piled at etch, how long the queue took
 * to drain after the chiller came back (hours, and it is a lot of hours),
 * how many lots the fault delayed and by how much, which dates it cost, and
 * the chamber-hours it took. Little's law is reported as a check on the
 * steady line, because it is the one relation everything else here rests
 * on: WIP equals throughput times cycle time, always.
 */

import { HOUSE_SYSTEMS } from '../house/model'
import { euvProductRoute, LELE, productRoute, QUANTUM_CHIP, SINGLE_DUV } from '../flow/routes'
import type { ProcessFlow } from '../flow/model'
import { Line, LOT_SIZE, meanCycleTime, meanOver, type LineOptions, type Sample } from './line'

export type Policy = 'reroute' | 'hold' | 'by-date'

export const POLICIES: Policy[] = ['reroute', 'hold', 'by-date']

/** The route the line runs: the full single-DUV product. */
export const LINE_FLOW: ProcessFlow = productRoute(SINGLE_DUV)

/**
 * A second product for the line that wants one: a bigger part, built on
 * litho-etch-litho-etch double patterning with fourteen metal layers, so it
 * visits the scanner and the etcher more often and takes a good deal longer
 * (176 operations, 118 raw hours against 130 and 91). Thirty per cent of
 * starts. A line carrying it takes longer to fill, so its warm-up is
 * longer too - Little's law is the check that it did.
 */
export const SECOND_PRODUCT = { flow: productRoute(LELE, 14), share: 0.3 }

/**
 * The quantum chip route as the LINE runs it.
 *
 * The route explorer prices QDT-24 per wafer: a cooldown is a day and a fridge
 * holds a handful of chips, so characterising every wafer is twenty fridge
 * hours each. A line does not do that to a 25-wafer lot - it would be three
 * weeks in a fridge. It cools a SAMPLE: two cooldowns a lot, chips drawn
 * across the wafers, and the lot is dispositioned on them. Forty-eight fridge
 * hours a lot, which is still by far the longest operation on any route here.
 */
const QDT_HOURS_PER_LOT = 48
const quantumLineRoute: ProcessFlow = {
  ...QUANTUM_CHIP,
  id: 'quantum-chip-line',
  nodes: QUANTUM_CHIP.nodes.map((n) => ('tool' in n && n.tool === 'QDT-24' ? { ...n, wafersPerHour: LOT_SIZE / QDT_HOURS_PER_LOT } : n)),
}

/**
 * The mix that puts EUV-22, SEM-23 and QDT-24 on the line.
 *
 * The reference line is one product on the immersion scanner, and every
 * number in the four scenarios is calibrated to it - so it stays the
 * default, untouched. This is the other line a student can ask for: a
 * quarter of the starts are a leading-edge part (EUV at the gate and the four
 * lowest metals, every exposure measured on the CD-SEM before it is etched),
 * and three per cent are superconducting qubit chips, which leave the front
 * end after eight operations and then spend two days in a fridge.
 *
 * The same chiller trip now lands on three families at once: the etcher
 * loses two chambers of three, the one EUV scanner goes down with it, and
 * every fridge in the farm stops.
 *
 * WHAT THE MODEL FOUND, which again is not what the story expected. The trip
 * is still an etch story - ETCH-01 stays the bottleneck and the pile is still
 * in front of it. What the new tools add is a queueing lesson that needs no
 * fault at all: the fridge farm runs at about HALF its capacity and its lots
 * wait anywhere from seven hours to two days for a fridge (it depends on the
 * seed: ninety lots is a small sample), while lots at the bottleneck, loaded
 * far harder, wait about one. Waiting scales with how long the thing in
 * front of you takes, not only with how busy the tool is - and the thing in
 * front of a qubit lot takes two days. The EUV scanner is the other half of
 * it: one tool, down one hour in eight, and the building has no second one
 * to send a lot to. `npm run physics:line` holds both.
 *
 * What it does NOT model: a fridge that stops for six hours has lost its
 * cooldown and starts again from 4 K. Here it is paused and resumes, which
 * flatters the farm.
 */
export const NEW_TOOLS_MIX = [
  { flow: euvProductRoute(), share: 0.25 },
  { flow: quantumLineRoute, share: 0.03 },
]

export type ProductMix = 'one' | 'two' | 'three'

const productsFor = (mix: ProductMix): Array<{ flow: ProcessFlow; share: number }> =>
  mix === 'two' ? [SECOND_PRODUCT] : mix === 'three' ? NEW_TOOLS_MIX : []

/** Forty-five days: several cycle times, so Little's law can hold - see runChillerTrip. */
const WARMUP_H = 45 * 24
/** Two weeks after: the lots an outage touched are still coming out a week later. */
const AFTER_H = 14 * 24

// ------------------------------------------------------------ measuring

/**
 * What one run cost against its control, lot by lot. Shared by every
 * scenario: the same ids are the same lots, since the arrivals stream is
 * shared, so lot 812 was released at the same moment in both runs and drew
 * the same service times.
 */
export interface Outcome {
  bottleneck: string
  scale: number
  tripAt: number
  restoreAt: number
  endAt: number
  /** Mean lots at the bottleneck over the 48 h before the event. */
  baselineWip: number
  /** Most lots at the bottleneck at any sample after it. */
  peakWip: number
  peakAt: number
  /**
   * Hours after the restore until the bottleneck's WIP was back within 10%
   * of its pre-event level, or null if it never was before the run ended.
   */
  drainH: number | null
  /** Lots completed between the event and the end of the run. */
  lotsOut: number
  /** Of those, lots that finished after their due time. */
  lotsLate: number
  /** The same fraction over the seven days before the event, for comparison. */
  lateFractionBefore: number
  /** Against the control: lots that finished later than they would have, and by how much. */
  lotsDelayed: number
  meanDelayH: number
  maxDelayH: number
  /** Every hour every lot finished later than in the control, added up. */
  delayLotHours: number
  /**
   * The same sum with the lots that finished EARLIER counted negative. An
   * outage reshuffles dispatch order downstream, so many lots move a little
   * either way and the positive sum alone measures the reshuffle, not the
   * outage; the net sum is the capacity the outage actually took.
   */
  netDelayLotHours: number
  /** Dates that were met in the control and missed here. */
  lateCaused: number
  hotOut: number
  hotLate: number
  hotMissedCaused: number
  /** Mean cycle time of lots completed in the seven days before the event [h]. */
  ctBeforeH: number
  /** Mean cycle time of lots completed after the event [h]. */
  ctAfterH: number
  /** The control's mean cycle time over the same window [h]. */
  ctControlH: number
  /** Little's law on the steady line: WIP against throughput × cycle time. */
  little: { wip: number; throughputPerH: number; ctH: number; ratio: number }
  samples: Sample[]
  /** The control's series over the same clock, for the dashed line under the others. */
  controlSamples: Sample[]
  tools: ReturnType<Line['toolReport']>
}

function measure(line: Line, control: Line, tripAt: number, restoreAt: number, endAt: number, warmupH: number): Outcome {
  const bottleneck = line.bottleneck
  const baselineWip = meanOver(line.samples, tripAt - 48, tripAt, 'bottleneckWip')
  let peakWip = 0
  let peakAt = tripAt
  let drainH: number | null = null
  for (const s of line.samples) {
    if (s.t < tripAt) continue
    if (s.bottleneckWip > peakWip) {
      peakWip = s.bottleneckWip
      peakAt = s.t
    }
    if (drainH === null && s.t > restoreAt && s.bottleneckWip <= baselineWip * 1.1) drainH = s.t - restoreAt
  }

  const inWindow = (l: Line, from: number, to: number) => l.completed.filter((x) => x.finishedAt! >= from && x.finishedAt! < to)
  const before = inWindow(line, tripAt - 7 * 24, tripAt)
  const after = inWindow(line, tripAt, endAt)
  const late = (lots: typeof after) => lots.filter((l) => l.finishedAt! > l.dueAt).length
  const hotAfter = after.filter((l) => l.hot)

  let lotsDelayed = 0
  let delaySum = 0
  let netSum = 0
  let maxDelayH = 0
  let lateCaused = 0
  let hotMissedCaused = 0
  for (const lot of after) {
    const twin = control.lots[lot.id - 1]
    if (!twin || twin.finishedAt === null) continue
    const delay = lot.finishedAt! - twin.finishedAt
    netSum += delay
    if (delay > 0.5) {
      lotsDelayed++
      delaySum += delay
      maxDelayH = Math.max(maxDelayH, delay)
    }
    const missed = lot.finishedAt! > lot.dueAt && twin.finishedAt <= twin.dueAt
    if (missed) {
      lateCaused++
      if (lot.hot) hotMissedCaused++
    }
  }

  // Little's law over the last two weeks of the warm-up, when the line is steady.
  const steadyFrom = warmupH - 14 * 24
  const steady = inWindow(line, steadyFrom, warmupH)
  const wip = meanOver(line.samples, steadyFrom, warmupH, 'wip')
  const throughputPerH = steady.length / (14 * 24)
  const ctH = meanCycleTime(line, steadyFrom, warmupH)

  return {
    bottleneck,
    scale: line.scale,
    tripAt,
    restoreAt,
    endAt,
    baselineWip,
    peakWip,
    peakAt,
    drainH,
    lotsOut: after.length,
    lotsLate: late(after),
    lateFractionBefore: before.length > 0 ? late(before) / before.length : 0,
    lotsDelayed,
    meanDelayH: lotsDelayed > 0 ? delaySum / lotsDelayed : 0,
    maxDelayH,
    delayLotHours: delaySum,
    netDelayLotHours: netSum,
    lateCaused,
    hotOut: hotAfter.length,
    hotLate: late(hotAfter),
    hotMissedCaused,
    ctBeforeH: meanCycleTime(line, tripAt - 7 * 24, tripAt),
    ctAfterH: meanCycleTime(line, tripAt, endAt),
    ctControlH: meanCycleTime(control, tripAt, endAt),
    little: { wip, throughputPerH, ctH, ratio: wip / (throughputPerH * ctH) },
    samples: line.samples,
    controlSamples: control.samples,
    tools: line.toolReport(),
  }
}

/** The trip and the restore: the PCW-cooled chambers down on every bottleneck tool. */
/**
 * Take down every chamber that rejects its heat into the house loop, on
 * every family on the line - which on the reference route is ETCH-01's two
 * and nothing else, and with EUV-22 and QDT-24 on the line is those as well.
 * Returns the chamber count across the building, for the hours lost.
 */
function scheduleTrip(line: Line, tripAt: number, restoreAt: number): number {
  const hit = line
    .familyTags()
    .map((tag) => ({ tag, cooled: line.spec(tag).pcwCooled ?? [] }))
    .filter((f) => f.cooled.length > 0)
  line.at(tripAt, () => {
    for (const f of hit) for (const c of f.cooled) line.setChamberDown(f.tag, c, true)
  })
  line.at(restoreAt, () => {
    for (const f of hit) for (const c of f.cooled) line.setChamberDown(f.tag, c, false)
  })
  return hit.reduce((n, f) => n + f.cooled.length * line.familySize(f.tag), 0)
}

// ---------------------------------------------------------- the trip

export interface ChillerTripOptions {
  policy: Policy
  /** Load on the bottleneck before the trip. */
  load?: number
  seed?: number
  /** Run the second product alongside the first - see SECOND_PRODUCT. */
  twoProducts?: boolean
  /** Which products the line carries - see NEW_TOOLS_MIX. Wins over `twoProducts`. */
  mix?: ProductMix
  /** Bays in the building - see LineOptions.scale. */
  scale?: number
  /** Hours the line runs before the trip, to reach steady state. */
  warmupH?: number
  /** Hours after the restore the run continues, to watch the drain. */
  afterH?: number
  /**
   * Hours from trip to restore. Defaults to the house module's own figure
   * for getting the chilled-water plant back, which is what facilities quote
   * when the call is made promptly. A call made late adds to it.
   */
  restoreH?: number
  flow?: ProcessFlow
}

export interface ChillerTripResult extends Outcome {
  policy: Policy
  load: number
  seed: number
  /** Each product's raw process time, product 0 first - one entry on a single-product line. */
  rawHoursByProduct: number[]
  /** Chamber-hours the fault took out of the building, every bay counted. */
  chamberHoursLost: number
}

export function runChillerTrip(opts: ChillerTripOptions): ChillerTripResult {
  const load = opts.load ?? 0.85
  const seed = opts.seed ?? 1
  const mix: ProductMix = opts.mix ?? (opts.twoProducts ? 'two' : 'one')
  // A longer route takes longer to fill; so does a line with a two-day
  // fridge step at the end of one of its routes.
  const warmupH = opts.warmupH ?? (mix === 'one' ? WARMUP_H : 75 * 24)
  const afterH = opts.afterH ?? AFTER_H
  const restoreH = opts.restoreH ?? HOUSE_SYSTEMS.pcw.hours
  const lineOpts: LineOptions = { seed, load, scale: opts.scale, products: productsFor(mix) }
  const flow = opts.flow ?? LINE_FLOW

  // Two in the morning of the day after the warm-up.
  const tripAt = warmupH + 2
  const restoreAt = tripAt + restoreH
  const endAt = restoreAt + afterH

  const line = new Line(flow, lineOpts)
  const cooled = scheduleTrip(line, tripAt, restoreAt)
  line.at(tripAt, () => {
    if (opts.policy === 'hold') line.setHoldStarts(true)
    if (opts.policy === 'by-date') line.setDispatch(line.bottleneck, 'by-date')
  })
  line.at(restoreAt, () => {
    if (opts.policy === 'hold') line.setHoldStarts(false)
  })
  line.run(endAt)

  const control = new Line(flow, lineOpts)
  control.run(endAt)

  return {
    ...measure(line, control, tripAt, restoreAt, endAt, warmupH),
    policy: opts.policy,
    load,
    seed,
    rawHoursByProduct: line.rawHoursByProduct,
    chamberHoursLost: cooled * restoreH,
  }
}

// ---------------------------------------------------- the queue-time window

export type QtimePolicy = 'run' | 'gate'

/**
 * The gate holds an exposure when the bound-for etcher's queue, over the
 * chambers it has up, would take more than this fraction of the window.
 * Below one, because the queue's wait is a mean and the window is a limit.
 */
export const GATE_FACTOR = 0.6

export const QTIME_POLICIES: QtimePolicy[] = ['run', 'gate']

export interface QtimeOptions {
  policy: QtimePolicy
  load?: number
  seed?: number
  scale?: number
  /** Hours a lot may wait between exposure and etch. */
  windowH?: number
  /** An optional WIP cap on the bound-for etcher, on top of the gate on its chambers. 0 for none. */
  gateMaxWip?: number
  restoreH?: number
  warmupH?: number
  afterH?: number
  flow?: ProcessFlow
}

export interface QtimeResult extends Outcome {
  policy: QtimePolicy
  load: number
  seed: number
  windowH: number
  gateMaxWip: number
  chamberHoursLost: number
  /** Lots sent back to the scanner for missing the window, after the trip. */
  reworks: number
  /** The same on the control - the window costs a few even on a healthy line. */
  reworksControl: number
  /** Scanner hours the reworks cost, after the trip. */
  reworkHours: number
}

/**
 * The chiller trip on a route with a queue-time window between exposure and
 * etch. Every lot exposed and waiting for a chamber that is down is a lot
 * whose resist is ageing. Under `run` the lots time out and go back to the
 * scanner; under `gate` a lot is not exposed while the etcher it is bound
 * for could not etch it within the window - its queue over its chambers up
 * is the wait - so it waits BEFORE the clock starts. See
 * LineOptions.gateFactor for the two gates that were tried first.
 */
export function runQtime(opts: QtimeOptions): QtimeResult {
  const load = opts.load ?? 0.85
  const seed = opts.seed ?? 1
  const windowH = opts.windowH ?? 8
  const gateMaxWip = opts.gateMaxWip ?? 0
  const warmupH = opts.warmupH ?? WARMUP_H
  const afterH = opts.afterH ?? AFTER_H
  const restoreH = opts.restoreH ?? HOUSE_SYSTEMS.pcw.hours
  const flow = opts.flow ?? LINE_FLOW
  const lineOpts: LineOptions = {
    seed,
    load,
    scale: opts.scale,
    qtimeWindowH: windowH,
    gateFactor: opts.policy === 'gate' ? GATE_FACTOR : 0,
    gateMaxWip: opts.policy === 'gate' ? gateMaxWip : 0,
  }

  const tripAt = warmupH + 2
  const restoreAt = tripAt + restoreH
  const endAt = restoreAt + afterH

  const line = new Line(flow, lineOpts)
  const cooled = scheduleTrip(line, tripAt, restoreAt)
  let reworksAtTrip = 0
  let reworkHoursAtTrip = 0
  line.at(tripAt, () => {
    reworksAtTrip = line.reworks
    reworkHoursAtTrip = line.reworkHours
  })
  line.run(endAt)

  const control = new Line(flow, lineOpts)
  let controlAtTrip = 0
  control.at(tripAt, () => (controlAtTrip = control.reworks))
  control.run(endAt)

  return {
    ...measure(line, control, tripAt, restoreAt, endAt, warmupH),
    policy: opts.policy,
    load,
    seed,
    windowH,
    gateMaxWip,
    chamberHoursLost: cooled * restoreH,
    reworks: line.reworks - reworksAtTrip,
    reworksControl: control.reworks - controlAtTrip,
    reworkHours: line.reworkHours - reworkHoursAtTrip,
  }
}

// -------------------------------------------------------- the excursion

export interface ExcursionOptions {
  /** Fraction of metrology visits that are inspected. */
  rate: number
  load?: number
  seed?: number
  scale?: number
  /** The tool that goes wrong, and which bay's. */
  tag?: string
  bay?: number
  /** Hours to clean and requalify it once caught. */
  repairH?: number
  /** Hours an inspection adds to a metrology visit. */
  extraH?: number
  warmupH?: number
  afterH?: number
  flow?: ProcessFlow
}

export interface ExcursionResult extends Outcome {
  rate: number
  load: number
  seed: number
  tag: string
  bay: number
  startAt: number
  /** Hours from the tool going wrong to an inspection catching it, or null if never. */
  detectionDelayH: number | null
  /** Lots processed on the tool while it was wrong. */
  exposedLots: number
  /** Of those, lots that finished the route before it was caught - shipped wrong. */
  escapedLots: number
  /** Inspections performed after the excursion began. */
  inspections: number
  /** Mean queue wait at the inspection tool over the run, and its utilisation. */
  metWaitH: number
  metUtilisation: number
}

/**
 * A deposition tool goes silently out of control at two in the morning.
 * Nothing on the floor says so. The metrology tool inspects a fraction of
 * its visits; an inspected lot that went through the bad tool is what
 * catches it. The control is the same line with the same inspection rate
 * and no excursion, so the cycle-time cost of sampling is in both and the
 * difference is the excursion's own.
 */
export function runExcursion(opts: ExcursionOptions): ExcursionResult {
  const load = opts.load ?? 0.85
  const seed = opts.seed ?? 1
  const tag = opts.tag ?? 'DEP-02'
  const bay = opts.bay ?? 3
  const repairH = opts.repairH ?? 8
  const extraH = opts.extraH ?? 0.5
  const warmupH = opts.warmupH ?? WARMUP_H
  const afterH = opts.afterH ?? 7 * 24
  const flow = opts.flow ?? LINE_FLOW
  const startAt = warmupH + 2
  const endAt = startAt + afterH
  const inspection = { tag: 'MET-05', rate: opts.rate, extraH }

  const line = new Line(flow, { seed, load, scale: opts.scale, inspection, excursion: { tag, bay, startAt, repairH } })
  let inspectionsAtStart = 0
  line.at(startAt, () => (inspectionsAtStart = line.inspections))
  line.run(endAt)

  const control = new Line(flow, { seed, load, scale: opts.scale, inspection })
  control.run(endAt)

  const met = line.toolReport().find((t) => t.tag === 'MET-05')!
  return {
    ...measure(line, control, startAt, line.detectedAt ?? endAt, endAt, warmupH),
    rate: opts.rate,
    load,
    seed,
    tag,
    bay,
    startAt,
    detectionDelayH: line.detectedAt === null ? null : line.detectedAt - startAt,
    exposedLots: line.exposedLots,
    escapedLots: line.escapedLots,
    inspections: line.inspections - inspectionsAtStart,
    metWaitH: met.meanWaitH,
    metUtilisation: met.utilisation,
  }
}

export interface ExcursionStudy {
  rate: number
  seeds: number[]
  runs: Array<{ seed: number; detectionDelayH: number | null; exposedLots: number; metWaitH: number; ctAfterH: number; lotsDelayed: number }>
  /** Mean detection delay over the seeds where it was caught, in hours. */
  meanDelayH: number
  meanExposed: number
  worstExposed: number
  /** Seeds where the excursion was never caught in the week. */
  missed: number
  meanMetWaitH: number
  meanCtH: number
  inspectionsPerDay: number
  metUtilisation: number
}

/**
 * The same sampling rate over several seeds. One detection is one draw -
 * which lot happened to be sampled - and a single seed can put 5% ahead of
 * 25%. The mean is what the rate actually buys.
 */
export function runExcursionStudy(rate: number, seeds: number[], opts: Omit<ExcursionOptions, 'rate' | 'seed'> = {}): ExcursionStudy {
  const results = seeds.map((seed) => runExcursion({ ...opts, rate, seed }))
  const runs = results.map((r) => ({
    seed: r.seed,
    detectionDelayH: r.detectionDelayH,
    exposedLots: r.exposedLots,
    metWaitH: r.metWaitH,
    ctAfterH: r.ctAfterH,
    lotsDelayed: r.lotsDelayed,
  }))
  const caught = runs.filter((r) => r.detectionDelayH !== null)
  const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : NaN)
  const afterH = (results[0].endAt - results[0].startAt) / 24
  return {
    rate,
    seeds,
    runs,
    meanDelayH: mean(caught.map((r) => r.detectionDelayH!)),
    meanExposed: mean(runs.map((r) => r.exposedLots)),
    worstExposed: Math.max(...runs.map((r) => r.exposedLots)),
    missed: runs.length - caught.length,
    meanMetWaitH: mean(runs.map((r) => r.metWaitH)),
    meanCtH: mean(runs.map((r) => r.ctAfterH)),
    inspectionsPerDay: mean(results.map((r) => r.inspections)) / afterH,
    metUtilisation: mean(results.map((r) => r.metUtilisation)),
  }
}

// ------------------------------------------------------- the maintenance

export type PmPolicy = 'now' | 'defer'

export const PM_POLICIES: PmPolicy[] = ['now', 'defer']

export interface PmOptions {
  policy: PmPolicy
  load?: number
  seed?: number
  scale?: number
  /** The chamber due: the bottleneck's, in one bay. */
  bay?: number
  chamber?: number
  /** Hours the planned service takes. */
  pmHours?: number
  /** The worn chamber's MTBF while the service is deferred, and how long its failure takes to fix. */
  wornMtbfH?: number
  wornMttrH?: number
  warmupH?: number
  afterH?: number
  flow?: ProcessFlow
}

export interface PmResult extends Outcome {
  policy: PmPolicy
  load: number
  seed: number
  /** Hours the chamber in question was down over the window - service, failures and all. */
  chamberDownH: number
  /** The same chamber's down hours in the control, which neither services nor wears it. */
  controlDownH: number
  /** Whether the deferred chamber failed in the window - the bet lost. Always false under `now`. */
  failed: boolean
  /** The table's MTBF and MTTR for that chamber, for the note. */
  mtbfH: number
  mttrH: number
  wornMtbfH: number
  wornMttrH: number
  pmHours: number
}

/**
 * One chamber of one etcher is due its maintenance at two in the morning.
 * `now` takes it down for the planned hours. `defer` runs on with a worn
 * chamber - MTBF cut to `wornMtbfH` until its next repair, which is the
 * service done the hard way and takes `wornMttrH`, twice the table's, as
 * an unplanned repair of a worn chamber does. The control neither services
 * nor wears it. Down hours are reported for the chamber over the week, not
 * against the control: the deferred chamber's failures are a different
 * draw from the control's, so a lot-by-lot difference on one chamber is
 * noise; the study below averages over seeds instead.
 */
export function runPm(opts: PmOptions): PmResult {
  const load = opts.load ?? 0.85
  const seed = opts.seed ?? 1
  const bay = opts.bay ?? 0
  const chamber = opts.chamber ?? 2
  const pmHours = opts.pmHours ?? 4
  const wornMtbfH = opts.wornMtbfH ?? 12
  const spec0 = new Line(opts.flow ?? LINE_FLOW, { seed, load, scale: opts.scale }).spec('ETCH-01')
  const wornMttrH = opts.wornMttrH ?? 2 * (spec0.mttrH ?? 4)
  const warmupH = opts.warmupH ?? WARMUP_H
  const afterH = opts.afterH ?? 7 * 24
  const flow = opts.flow ?? LINE_FLOW
  const lineOpts: LineOptions = { seed, load, scale: opts.scale }
  const at = warmupH + 2
  const endAt = at + afterH

  const line = new Line(flow, lineOpts)
  const tag = line.bottleneck
  if (opts.policy === 'now') line.schedulePm(tag, bay, chamber, at, pmHours)
  else line.deferPm(tag, bay, chamber, at, wornMtbfH, wornMttrH)
  let downAtStart = 0
  line.at(at, () => (downAtStart = line.chamberDownHours(tag, bay, chamber)))
  line.run(endAt)

  const control = new Line(flow, lineOpts)
  let controlDownAtStart = 0
  control.at(at, () => (controlDownAtStart = control.chamberDownHours(tag, bay, chamber)))
  control.run(endAt)

  const spec = line.spec(tag)
  const down = line.chamberDownHours(tag, bay, chamber) - downAtStart
  const controlDown = control.chamberDownHours(tag, bay, chamber) - controlDownAtStart
  return {
    ...measure(line, control, at, at + pmHours, endAt, warmupH),
    policy: opts.policy,
    load,
    seed,
    chamberDownH: down,
    controlDownH: controlDown,
    failed: line.wearFailedAt !== null,
    mtbfH: spec.mtbfH ?? 0,
    mttrH: spec.mttrH ?? 0,
    wornMtbfH,
    wornMttrH,
    pmHours,
  }
}

export interface PmStudy {
  policy: PmPolicy
  seeds: number[]
  /** Per seed: the chamber's down hours over the week, the control's, lots delayed, dates lost. */
  runs: Array<{ seed: number; chamberDownH: number; controlDownH: number; failed: boolean; lotsDelayed: number; delayLotHours: number; netDelayLotHours: number; lateCaused: number }>
  meanDownH: number
  worstDownH: number
  meanControlDownH: number
  /** Of the seeds, how many saw the deferred chamber fail in the week. */
  failures: number
  meanDelayed: number
  /** Mean total lot-hours of delay against the control, and the net of early and late. */
  meanDelayLotHours: number
  meanNetDelayLotHours: number
  meanLateCaused: number
  pmHours: number
  mtbfH: number
  mttrH: number
  wornMtbfH: number
  wornMttrH: number
}

/** The same decision over several seeds: a deferral is a bet, and a bet is judged on average. */
export function runPmStudy(policy: PmPolicy, seeds: number[], opts: Omit<PmOptions, 'policy' | 'seed'> = {}): PmStudy {
  let last: PmResult | null = null
  const runs = seeds.map((seed) => {
    const r = runPm({ ...opts, policy, seed })
    last = r
    return { seed, chamberDownH: r.chamberDownH, controlDownH: r.controlDownH, failed: r.failed, lotsDelayed: r.lotsDelayed, delayLotHours: r.delayLotHours, netDelayLotHours: r.netDelayLotHours, lateCaused: r.lateCaused }
  })
  const mean = (f: (r: (typeof runs)[number]) => number) => runs.reduce((s, r) => s + f(r), 0) / runs.length
  const l = last!
  return {
    policy,
    seeds,
    runs,
    meanDownH: mean((r) => r.chamberDownH),
    worstDownH: Math.max(...runs.map((r) => r.chamberDownH)),
    meanControlDownH: mean((r) => r.controlDownH),
    failures: runs.filter((r) => r.failed).length,
    meanDelayed: mean((r) => r.lotsDelayed),
    meanDelayLotHours: mean((r) => r.delayLotHours),
    meanNetDelayLotHours: mean((r) => r.netDelayLotHours),
    meanLateCaused: mean((r) => r.lateCaused),
    pmHours: l.pmHours,
    mtbfH: l.mtbfH,
    mttrH: l.mttrH,
    wornMtbfH: l.wornMtbfH,
    wornMttrH: l.wornMttrH,
  }
}

// ------------------------------------------------------- the curve

export interface OperatingPoint {
  load: number
  /** Mean cycle time of lots completed in the measured window [h]. */
  ctH: number
  /** As a multiple of the raw process time. */
  ctOverRaw: number
  wip: number
  /** Measured utilisation of the bottleneck over the whole run. */
  utilisation: number
}

/**
 * The operating curve: cycle time against load on the healthy line.
 *
 * The one curve every fab manager carries in their head. Flat and near the
 * raw process time at low load, then bending up, then vertical: at 95% a
 * queue that forms takes so long to clear that the next disturbance lands
 * on top of it. The chiller scenario at 85% and at 95% is two points on
 * this curve seen from inside.
 */
export function operatingCurve(
  loads: number[],
  opts: { seed?: number; hours?: number; flow?: ProcessFlow; scale?: number } = {},
): OperatingPoint[] {
  const hours = opts.hours ?? 60 * 24
  return loads.map((load) => {
    const line = new Line(opts.flow ?? LINE_FLOW, { seed: opts.seed ?? 1, load, scale: opts.scale })
    line.run(hours)
    const from = hours / 2
    const bn = line.toolReport().find((t) => t.tag === line.bottleneck)!
    const ctH = meanCycleTime(line, from, hours)
    return {
      load,
      ctH,
      ctOverRaw: ctH / line.rawHoursPerLot,
      wip: meanOver(line.samples, from, hours, 'wip'),
      utilisation: bn.utilisation,
    }
  })
}
